import type { WorldCapabilities } from "@uzverse/contracts";
import { UzError, newId } from "@uzverse/core";
import { checkPlacement, degToRad, quatToYaw, yawToQuat } from "@uzverse/world-scene";
import type { ParcelBounds, SceneNode } from "@uzverse/world-scene";
import type { WorldToolResult } from "./data.js";
import { footprintOf } from "./generators.js";
import { MOCK_IDS } from "./mock-data.js";
import type { MockWorldService } from "./mock.js";

/**
 * In-browser stand-ins for `GET /api/v1/world/capabilities` and the `world.moveBuilding` tool
 * (mock mode only; `MockWorldService.invoke` answers `POST /api/v1/world/tools/invoke`). They follow
 * the real rules: the owner of a plot may move their building inside it; anything else is denied;
 * overlaps and a stale `expectedVersion` are conflicts. Every invoke is recorded on
 * `window.__uzMockInvokes` so e2e can assert the payload.
 */

interface MockGlobals {
  __uzMockCaps?: Partial<WorldCapabilities>;
}

/** The user in the mock world owns the company of `MOCK_IDS.parcel`. */
export function mockCapabilities(): WorldCapabilities {
  const base: WorldCapabilities = {
    cameraModes: ["orbit", "blueprint", "first_person", "third_person", "fly"],
    defaultCameraMode: "orbit",
    canEditMap: false,
    stylableCompanyIds: [MOCK_IDS.company],
    arrangeableParcelIds: [MOCK_IDS.parcel],
    parcels: [
      {
        id: MOCK_IDS.parcel,
        companyId: MOCK_IDS.company,
        name: "Demo Café",
        bounds: { minX: 80, minZ: -30, maxX: 140, maxZ: 30 },
      },
    ],
  };
  return { ...base, ...(globalThis as MockGlobals).__uzMockCaps };
}

const boundsOf = (n: SceneNode): ParcelBounds | null => {
  if (n.geometry.kind !== "procedural") return null;
  const b = n.geometry.params["bounds"] as Partial<ParcelBounds> | undefined;
  return b &&
    typeof b.minX === "number" &&
    typeof b.minZ === "number" &&
    typeof b.maxX === "number" &&
    typeof b.maxZ === "number"
    ? (b as ParcelBounds)
    : null;
};

/** `world.moveBuilding` against the mock world; `moves` remembers the node for `history.undo`. */
export function moveBuildingMock(
  world: MockWorldService,
  moves: Map<string, SceneNode>,
  input: unknown,
): WorldToolResult {
  const { placementId, position, yawDeg, expectedVersion } = (input ?? {}) as {
    placementId?: string;
    position?: [number, number, number];
    yawDeg?: number;
    expectedVersion?: number;
  };
  const node = placementId ? world.nodeById(placementId) : undefined;
  if (!node || node.entityType !== "world.placement" || !position) {
    throw new UzError("not_found", "no such building");
  }
  if (expectedVersion !== undefined && expectedVersion !== node.version) {
    throw new UzError("conflict", "the building changed in the meantime");
  }
  const [x, y, z] = position;
  if (y !== 0) throw new UzError("invalid_input", "buildings stand on the ground: y must be 0");
  if (node.geometry.kind !== "procedural") throw new UzError("invalid_input", "not a building");
  const f = footprintOf(node.geometry.generator, node.geometry.params);
  const yawRad =
    yawDeg !== undefined ? degToRad(yawDeg) : (quatToYaw(node.transform.rotation) ?? 0);

  const nodes = [...world.heldNodes()];
  const parcels = nodes
    .filter((n) => n.entityType === "world.parcel" && boundsOf(n))
    .map((n) => ({ id: n.id, bounds: boundsOf(n)!, owner: n.ownerCompanyId }));
  const footprints = nodes
    .filter((n) => n.kind === "building" && n.geometry.kind === "procedural")
    .map((n) => {
      const g = n.geometry as { generator: string; params: Record<string, unknown> };
      const fp = footprintOf(g.generator, g.params);
      return {
        id: n.id,
        kind: "building" as const,
        x: n.transform.position[0],
        z: n.transform.position[2],
        yawRad: quatToYaw(n.transform.rotation) ?? 0,
        width: fp.width,
        depth: fp.depth,
      };
    });
  const own = parcels.find((p) => p.owner !== null && p.owner === node.ownerCompanyId);
  const candidate = {
    kind: "building" as const,
    selfId: node.id,
    x,
    z,
    yawRad,
    width: f.width,
    depth: f.depth,
  };
  const placement = { parcels: parcels.map((p) => ({ id: p.id, bounds: p.bounds })), footprints };
  const inside = own ? checkPlacement({ ...candidate, parcelId: own.id }, placement) : null;
  if (inside && (inside.ok || inside.reason === "overlap")) {
    if (!inside.ok) throw new UzError("conflict", "that spot overlaps another building");
  } else {
    if (!mockCapabilities().canEditMap) {
      throw new UzError("permission_denied", "permission denied", { missing: ["world.map.edit"] });
    }
    const free = checkPlacement({ ...candidate, parcelId: null }, placement);
    if (!free.ok) {
      throw new UzError(free.reason === "overlap" ? "conflict" : "invalid_input", free.reason);
    }
  }
  const correlationId = newId();
  moves.set(correlationId, node);
  const updated = {
    ...node,
    transform: { position: [x, 0, z] as [number, number, number], rotation: yawToQuat(yawRad) },
    version: node.version + 1,
  };
  world.apply([{ op: "upsert", node: updated, cause: { actor: "human", taskId: null } }]);
  return { output: { placementId: node.id, version: updated.version }, correlationId };
}
